import { useEffect, useMemo, useState } from "react";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { RoomIcon } from "@/components/home/room-icon";
import { Wordmark } from "@/components/shell/wordmark";
import { Splash } from "@/components/shell/splash";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { boardName } from "@/i18n/catalog";
import { linkIn } from "@/lib/capture-product";
import { cn } from "@/lib/utils";
import { said } from "@/lib/errors";

type ShareSearch = {
  url?: string;
  title?: string;
  text?: string;
  // Product image, sent by the "Save to Ours" bookmarklet.
  image?: string;
};

export const Route = createFileRoute("/share")({
  validateSearch: (search): ShareSearch => ({
    url: typeof search.url === "string" ? search.url : undefined,
    title: typeof search.title === "string" ? search.title : undefined,
    text: typeof search.text === "string" ? search.text : undefined,
    image: typeof search.image === "string" ? search.image : undefined,
  }),
  component: SharePage,
});

function SharePage() {
  const { t } = useTranslation("share");
  return (
    <>
      <AuthLoading>
        <Splash subtitle={t("splash.oneMoment")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <SignedInShare />
      </Authenticated>
      <Toaster />
    </>
  );
}

function SignedInShare() {
  const { t } = useTranslation("share");
  const navigate = useNavigate();
  const search = useSearch({ from: "/share" });
  const data = useQuery(api.boards.listForMyWorkspace);
  const createFromUrl = useMutation(api.items.createFromUrl);
  const [busyId, setBusyId] = useState<string | null>(null);

  const sharedUrl = useMemo(
    () => linkIn(search.url ?? search.text ?? ""),
    [search.url, search.text],
  );

  if (data === undefined) return <Splash subtitle={t("splash.loadingBoards")} />;
  if (data === null) return <RedirectTo to="/onboarding" />;

  if (!sharedUrl) {
    return (
      <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <Wordmark size="lg" as="div" />
        <p className="text-sm text-ink-3">{t("noUrl")}</p>
        <Button size="sm" onClick={() => navigate({ to: "/" })}>
          {t("backHome")}
        </Button>
      </main>
    );
  }

  async function onPick(board: Doc<"boards">) {
    if (busyId) return;
    setBusyId(board._id);
    try {
      await createFromUrl({
        boardId: board._id as Id<"boards">,
        url: sharedUrl!,
        titleHint: search.title,
        imageHint: search.image,
      });
      navigate({ to: "/boards/$boardId", params: { boardId: board._id } });
    } catch (error) {
      toast.error(said(error, t("saveFailed")));
      setBusyId(null);
    }
  }

  const rooms = data.boards.filter((b) => b.kind === "room");
  const freeform = data.boards.filter((b) => b.kind === "freeform");

  return (
    <main className="mx-auto min-h-svh w-full max-w-3xl px-6 pb-24">
      <div className="flex items-center justify-between border-b border-line py-4">
        <Wordmark size="sm" />
        <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">
          {t("eyebrow")}
        </span>
      </div>

      <header className="grid gap-3 py-10">
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05] text-ink">
          <Trans
            t={t}
            i18nKey="title"
            components={{ em: <span className="italic" /> }}
          />
        </h1>
        <a
          href={sharedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex max-w-full items-center gap-1.5 text-xs text-ink-3 hover:text-ink truncate"
        >
          <ArrowUpRight className="size-3 shrink-0" />
          <span className="truncate">{sharedUrl}</span>
        </a>
      </header>

      <div className="grid gap-10">
        {rooms.length > 0 ? (
          <PickerSection
            label={t("sections.rooms")}
            boards={rooms}
            busyId={busyId}
            onPick={onPick}
          />
        ) : null}
        {freeform.length > 0 ? (
          <PickerSection
            label={t("sections.themes")}
            boards={freeform}
            busyId={busyId}
            onPick={onPick}
          />
        ) : null}
      </div>
    </main>
  );
}

function PickerSection({
  label,
  boards,
  busyId,
  onPick,
}: {
  label: string;
  boards: Doc<"boards">[];
  busyId: string | null;
  onPick: (board: Doc<"boards">) => void;
}) {
  const { t } = useTranslation("share");
  const { t: tc } = useTranslation();
  return (
    <section className="grid gap-2">
      <h2 className="border-b border-line-strong pb-2 text-2xs uppercase tracking-[0.18em] text-ink-2">
        {label} — {String(boards.length).padStart(2, "0")}
      </h2>
      <ul className="stagger">
        {boards.map((board, idx) => (
          <li key={board._id}>
            <button
              type="button"
              disabled={busyId !== null}
              onClick={() => onPick(board)}
              className={cn(
                "group grid w-full grid-cols-[2.5rem_1.25rem_1fr_auto] items-center gap-3 border-b border-line py-3.5 text-start",
                "transition-colors disabled:opacity-50",
                busyId === board._id
                  ? "bg-paper-2"
                  : "hover:bg-paper-2/60",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
              )}
            >
              <span className="text-2xs num text-ink-4 tracking-wider tabular-nums">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <RoomIcon
                roomType={board.roomType}
                icon={board.icon}
                className="size-4 text-ink-3 group-hover:text-ink transition-colors"
              />
              <span className="font-display text-lg sm:text-xl text-ink truncate leading-snug">
                {boardName(board.name)}
              </span>
              <span className="flex items-center gap-2 text-xs text-ink-3">
                {busyId === board._id ? (
                  <span className="num">{tc("actions.saving")}</span>
                ) : (
                  <>
                    <span>{t("saveHere")}</span>
                    <CheckCircle2 className="size-3.5 -translate-x-1 rtl:translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RedirectTo({ to }: { to: "/onboarding" }) {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to });
  }, [navigate, to]);
  return <Splash subtitle={t("redirecting")} />;
}
